// ── Canonical Contractor Identity Resolver (P11a hardened) ──
// Single source of truth for "who is the current contractor".
//
// Priority (highest first):
//   1. auth session  (future — not wired in beta)
//   2. localStorage session  ("contractor_id")
//   3. none          → blocking state (no silent fallback)
//
// Hard rules (P11a):
//   • NEVER silently fall back to a stub contractor.
//   • NEVER swap to a different contractor when the requested one is missing.
//   • Tests must explicitly seed `contractor_id` — no hidden test fallback.

import type { Contractor } from "@/data/types";

export const CONTRACTOR_SESSION_KEY = "contractor_id";
/** @deprecated legacy key for back-compat reads — writes use CONTRACTOR_SESSION_KEY. */
export const CONTRACTOR_OVERRIDE_KEY = "koredor.contractor.override";

export type ContractorIdentitySource =
  | "auth"
  | "localStorage_session"
  | "none";

export interface ResolvedContractorIdentity {
  /** Empty string when source === "none". */
  id: string;
  record?: Contractor;
  source: ContractorIdentitySource;
  /** Display name for headers. Empty when unresolved. */
  displayName: string;
  /**
   * Legacy lookup key matching `Job.assignedSubcontractor` (company name).
   * @deprecated New logic should use `id`.
   */
  matchKey: string;
  firstName?: string;
  /** True when no usable identity was resolved. */
  isUnresolved: boolean;
}

function readLocalSession(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v =
      window.localStorage.getItem(CONTRACTOR_SESSION_KEY) ||
      window.localStorage.getItem(CONTRACTOR_OVERRIDE_KEY); // legacy
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function setContractorSession(id: string): void {
  try {
    window.localStorage.setItem(CONTRACTOR_SESSION_KEY, id);
  } catch {
    /* noop */
  }
}

export function clearContractorSession(): void {
  try {
    window.localStorage.removeItem(CONTRACTOR_SESSION_KEY);
    window.localStorage.removeItem(CONTRACTOR_OVERRIDE_KEY);
  } catch {
    /* noop */
  }
}

export function resolveCurrentContractor(
  contractors: Contractor[],
): ResolvedContractorIdentity {
  // 1. auth — placeholder for future wiring
  // 2. localStorage session
  const sessionId = readLocalSession();
  if (sessionId) {
    const record = contractors.find((c) => c.id === sessionId);
    if (record) return build("localStorage_session", record.id, record);
    // ID set but unknown → unresolved (do NOT silently swap).
    return unresolved();
  }
  // 3. none
  return unresolved();
}

function build(
  source: ContractorIdentitySource,
  id: string,
  record: Contractor,
): ResolvedContractorIdentity {
  return {
    id,
    record,
    source,
    displayName: record.companyName ?? id,
    matchKey: record.companyName ?? id,
    firstName:
      record.contactFirstName?.trim() ||
      record.contactName?.trim().split(/\s+/)[0] ||
      undefined,
    isUnresolved: false,
  };
}

function unresolved(): ResolvedContractorIdentity {
  return {
    id: "",
    record: undefined,
    source: "none",
    displayName: "",
    matchKey: "",
    firstName: undefined,
    isUnresolved: true,
  };
}
